import { isEmptyPhone } from 'src/components/forms/validators';

export type OrgContact = {
  name?: string | null;
  title?: string | null;
  email?: string | null;
  phone?: string | null;
  description?: string | null;
  emergencyContact?: boolean | null;
  [key: string]: unknown;
};

export type OrgContactInfo = { CFO: OrgContact; alternateContact: OrgContact; otherContacts: OrgContact[]; [key: string]: unknown };

export type ContactRole = 'cfo' | 'alternate' | 'other';

/** One table row: `path` is where its contact lives in the record, for setField and error lookups. */
export type ContactRow = { role: ContactRole; key: string; path: string; contact: OrgContact; otherIndex: number | null };

export const MAX_OTHER_CONTACTS = 10;

// emergencyContact is always a boolean, so switching the toggle on and off again is no change.
function asContact(value: unknown): OrgContact {
  const contact = value && typeof value === 'object' && !Array.isArray(value) ? (value as OrgContact) : {};
  return { ...contact, emergencyContact: !!contact.emergencyContact };
}

/**
 * The stored OrgContactInformation shape with its two fixed contacts and the others list always
 * present. Applied on load, so the loaded record and the draft share one shape and an edit typed and
 * cleared again compares equal. Unknown keys are kept, at both levels.
 */
export function normalizeContactInfo(info: unknown): OrgContactInfo {
  const source = info && typeof info === 'object' && !Array.isArray(info) ? (info as Record<string, unknown>) : {};
  return {
    ...source,
    CFO: asContact(source.CFO),
    alternateContact: asContact(source.alternateContact),
    otherContacts: Array.isArray(source.otherContacts) ? source.otherContacts.map(asContact) : [],
  };
}

export function contactRows(info: OrgContactInfo): ContactRow[] {
  return [
    { role: 'cfo', key: 'cfo', path: 'orgContactInfo.CFO', contact: info.CFO, otherIndex: null },
    { role: 'alternate', key: 'alternate', path: 'orgContactInfo.alternateContact', contact: info.alternateContact, otherIndex: null },
    ...info.otherContacts.map((contact, index) => ({
      role: 'other' as ContactRole,
      key: `other-${index}`,
      path: `orgContactInfo.otherContacts.${index}`,
      contact,
      otherIndex: index,
    })),
  ];
}

const CONTACT_TEXT_FIELDS = ['name', 'title', 'email', 'phone', 'description'];

/** An "Other" row added and never filled in: no text (a phone mask with nothing typed counts as empty) and not flagged. */
export function isBlankContact(contact: OrgContact): boolean {
  return !contact.emergencyContact && CONTACT_TEXT_FIELDS.every(field => isEmptyPhone(contact[field]));
}

/** The contacts to post: "Other" rows left completely empty are dropped rather than stored. */
export function withoutBlankContacts(info: OrgContactInfo): OrgContactInfo {
  const normalized = normalizeContactInfo(info);
  return { ...normalized, otherContacts: normalized.otherContacts.filter(contact => !isBlankContact(contact)) };
}

export function newOtherContact(): OrgContact {
  return { name: '', title: '', email: '', phone: '', description: '', emergencyContact: false };
}
